import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useParams } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { LanguageProvider } from './context/LanguageContext';

// Components
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import StarBackground from './components/common/StarBackground';
import CookieConsent from './components/common/CookieConsent';

// Pages
import Home from './pages/Home';
import Kundli from './pages/Kundli';
import Horoscope from './pages/Horoscope';
import HoroscopeDetail from './pages/HoroscopeDetail';
import Zodiac from './pages/Zodiac';
import Blog from './pages/Blog';
import BlogDetail from './pages/BlogDetail';
import Contact from './pages/Contact';
import MatchMaking from './pages/MatchMaking';
import Panchangam from './pages/Panchangam';
import HinduFestivals from './pages/HinduFestivals';
import TamilFestivals from './pages/TamilFestivals';
import TeluguFestivals from './pages/TeluguFestivals';
import ImportantDates from './pages/ImportantDates';
import Consultation from './pages/Consultation';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import Refund from './pages/Refund';
import JathagamInTamil from './pages/JathagamInTamil';
import JathagamPoruthamTamil from './pages/JathagamPoruthamTamil';
import DivyaDesams from './pages/DivyaDesams';
import DivyaDesamDetail from './pages/DivyaDesamDetail';
import NotFound from './pages/NotFound';

// Admin
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';

// Programmatic SEO
import PSEOPage from './pages/PSEOPage';

// Styles
import './App.css';

/* Reset scroll to top whenever the route path changes. Without this,
   React Router preserves the previous page's scroll position, so users land
   mid-page after navigating. */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    if (typeof window === 'undefined') return;
    // Instant — smooth scroll on route change feels jarring
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);
  return null;
}

/* /blog/:slug dispatcher — tries the PSEO store first, falls back to the
   static blog detail page if the slug isn't a generated page. */
function BlogOrPSEO() {
  const { slug } = useParams();
  // undefined = still checking, null = not a PSEO page, object = PSEO page
  const [pseoData, setPseoData] = useState(undefined);

  useEffect(() => {
    let cancelled = false;
    setPseoData(undefined);
    fetch(`/api/pages/${slug}`)
      .then((r) => r.json())
      .then((j) => { if (!cancelled) setPseoData(j && j.success ? j.data : null); })
      .catch(() => { if (!cancelled) setPseoData(null); });
    return () => { cancelled = true; };
  }, [slug]);

  if (pseoData === undefined) {
    return (
      <div style={{
        minHeight: '60vh',
        display: 'grid',
        placeItems: 'center',
      }}>
        <div style={{
          width: 36, height: 36,
          border: '3px solid #f0e0d0',
          borderTopColor: '#ff6f00',
          borderRadius: '50%',
          animation: 'spin 0.9s linear infinite',
        }} />
      </div>
    );
  }

  if (pseoData) return <PSEOPage prefetched={pseoData} />;
  return <BlogDetail />;
}

function AppShell() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  return (
    <div className="App">
      <ScrollToTop />
      {!isAdmin && <StarBackground />}
      {!isAdmin && <Navbar />}
      <AnimatePresence mode="wait">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/online-horoscope-generator" element={<Kundli />} />
          <Route path="/jathagam-in-tamil" element={<JathagamInTamil />} />
          <Route path="/horoscope" element={<Horoscope />} />
          <Route path="/horoscope/:sign" element={<HoroscopeDetail />} />
          <Route path="/zodiac" element={<Zodiac />} />
          <Route path="/zodiac/:sign" element={<HoroscopeDetail />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogOrPSEO />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/match-making" element={<MatchMaking />} />
          <Route path="/jathagam-porutham-in-tamil-for-marriage" element={<JathagamPoruthamTamil />} />
          <Route path="/panchangam" element={<Panchangam />} />
          <Route path="/hindu-festivals" element={<HinduFestivals />} />
          <Route path="/tamil-festivals" element={<TamilFestivals />} />
          <Route path="/telugu-festivals" element={<TeluguFestivals />} />
          <Route path="/important-dates" element={<ImportantDates />} />
          <Route path="/108-divya-desams" element={<DivyaDesams />} />
          <Route path="/108-divya-desams/:slug" element={<DivyaDesamDetail />} />
          <Route path="/consult" element={<Consultation />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/refund" element={<Refund />} />
          <Route path="/admin" element={<AdminLogin />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </AnimatePresence>
      {!isAdmin && <Footer />}
      {!isAdmin && <CookieConsent />}
    </div>
  );
}

function App() {
  return (
    <LanguageProvider>
      <Router>
        <AppShell />
      </Router>
    </LanguageProvider>
  );
}

export default App;
